import { useContext, useEffect, useState } from "react";
import { httpGet, httpPost } from "../hooks/http";
import { UserContext } from "../UserContext";

import eventIcon from "../img/dropdown-icons/EventDetails.svg";
import beverageIcon from "../img/dropdown-icons/Beverage.svg";
import financialsIcon from "../img/dropdown-icons/EventFinancials.svg";
import menusIcon from "../img/dropdown-icons/Menus.svg";
import miscellaneousIcon from "../img/dropdown-icons/Miscellaneous.svg";
import rentalIcon from "../img/dropdown-icons/Rental.svg";
import staffingIcon from "../img/dropdown-icons/Staffing.svg";

export const sections = {
  info: {
    id: 1000,
    name: "Event Details",
    icon: eventIcon,
  },
  menu: {
    id: 2000,
    name: (info) => info?.sectionLabels[0]?.name,
    icon: menusIcon,
  },
  beverage: {
    id: 3000,
    name: (info) => info?.sectionLabels[1]?.name,
    icon: beverageIcon,
  },
  rentals: {
    id: 4000,
    additionalSectionIds: [5000],
    name: (info) =>
      [info?.sectionLabels[2]?.name, info?.sectionLabels[3]?.name]
        .filter(Boolean)
        .join(" & "),
    icon: rentalIcon,
  },
  miscellaneous: {
    id: 6000,
    name: (info) => info?.sectionLabels[4]?.name,
    icon: miscellaneousIcon,
  },
  staff: {
    id: 7000,
    name: (info) => info?.sectionLabels[6]?.name,
    icon: staffingIcon,
  },
  financial: {
    id: 9000,
    name: "Event Financials",
    icon: financialsIcon,
    paymentFieldId: 5000,
  },
  overallComments: { id: 10000, name: "Overall Comments" },
};

export const useEventComments = (eventId) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { access_token, businessInfo } = useContext(UserContext);

  useEffect(() => {
    setIsLoading(true);
    const promises = [
      httpGet(`events/${eventId}/clientportalcomments`, access_token),
      httpGet(`clientPortal/events/${eventId}/components/menus`, access_token),
    ];
    Promise.all(promises).then(([commentResponse, eventMenuResponse]) => {
      const {
        response: { clientPortalComments },
      } = commentResponse;

      const {
        response: { eventMenus },
      } = eventMenuResponse;

      const _comments = Object.entries(sections).map(([k, p]) => {
        const d = {
          ...p,
          title:
            typeof p?.name === "function" ? p?.name(businessInfo) : p?.name,
          type: k,
          menus: p.id === sections.menu.id ? eventMenus : null,
          comments: clientPortalComments
            .filter(
              (comment) =>
                comment.sectionId === p.id ||
                (p?.additionalSectionIds || []).includes(comment.sectionId)
            )
            .sort((a, b) => a.sectionId - b.sectionId),
        };
        if (!d.menus?.length) {
          return d;
        }
        return {
          ...d,
          menus: d.menus.filter((menu) =>
            d.comments.some((comment) => comment.fieldId === menu.id)
          ),
        };
      });
      setComments(_comments);
      setIsLoading(false);
    });
  }, [eventId, access_token, businessInfo]);

  const useToggleCommentDone = async (comment) => {
    const isArchived = !comment?.isArchived;
    const response = await httpPost(
      `events/${eventId}/clientportalcomments/archive/${comment?.id}`,
      { ...comment, isArchived },
      access_token,
      "PUT"
    );
    if (response?.message) throw new Error(response.message);
    const {
      response: { eventClientPortal },
    } = response;
    if (!eventClientPortal) return;

    const newData = comments.map((p) => {
      if (
        p.id !== comment.sectionId &&
        !(p?.additionalSectionIds || []).includes(comment.sectionId)
      )
        return p;
      return {
        ...p,
        comments: p.comments.map((c) => {
          if (c.id !== comment.id) {
            if (
              isArchived &&
              !c?.isArchived &&
              c?.parentThreadId === comment?.threadId
            ) {
              return {
                ...c,
                isArchived: true,
              };
            }
            return c;
          }
          return {
            ...c,
            ...eventClientPortal,
          };
        }),
      };
    });
    setComments(newData);
  };

  const useReplyToComment = async (id, replyTo, replySubject, comment) => {
    const response = await httpPost(
      `events/${eventId}/clientportalcomments/${id}/reply`,
      {
        to: replyTo,
        subject: replySubject,
        content: comment,
      },
      access_token,
      "POST"
    );
    if (!response) return;
    if (response?.message) throw new Error(response.message);
    const { response: replyComment } = response;
    const newData = comments.map((p) => {
      if (
        p.id !== replyComment.sectionId &&
        !(p?.additionalSectionIds || []).includes(replyComment.sectionId)
      )
        return p;

      return {
        ...p,
        comments: p.comments
          .map((c) => {
            if (c.id !== id) {
              return c;
            }
            return {
              ...c,
              isArchived: true,
            };
          })
          .concat([replyComment]),
      };
    });
    setComments(newData);
  };
  const useAddComment = async (comment) => {
    const response = await httpPost(
      `events/${eventId}/clientportalcomments`,
      [comment],
      access_token
    );
    if (!response) return;
    if (response?.message) throw new Error(response.message);
    const {
      response: { comments: replies },
    } = response;
    const [replyComment] = replies.slice(-1);
    const newData = comments.map((p) => {
      if (
        p.id !== replyComment.sectionId &&
        !(p?.additionalSectionIds || []).includes(replyComment.sectionId)
      )
        return p;
      return {
        ...p,
        comments: p.comments.concat([replyComment]),
      };
    });
    setComments(newData);
  };

  const useMarkCommentsRead = async (commentIds) => {
    if (!commentIds?.length) {
      return;
    }
    const response = await httpPost(
      `events/${eventId}/clientportalcomments/archive`,
      commentIds,
      access_token
    );
    if (!response) return;
    if (response?.message) throw new Error(response.message);
    if (!response?.response) return;

    const newData = comments.map((p) => {
      return {
        ...p,
        comments: p.comments.map((c) => {
          if (!commentIds.includes(c?.id)) return c;
          return {
            ...c,
            isArchived: true,
          };
        }),
      };
    });
    setComments(newData);
  };

  return {
    comments,
    isLoading,
    useToggleCommentDone,
    useReplyToComment,
    useAddComment,
    useMarkCommentsRead,
  };
};
